import { useAuth } from '@clerk/react'
import type { editor } from 'monaco-editor'
import type { ComponentProps, ComponentType } from 'react'
import { lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { z } from 'zod'
import { useUserSettingsOptional } from '@/components/user-settings-context'
import { Sentry } from '@/lib/sentry-client'
import { trpc } from '@/lib/trpc.js'
import type { RunClassTestCase, RunTestCase } from '@/lib/types/test-cases'
import { useTheme } from '../../theme-provider'
import type { LanguageValue } from '../editor-languages'
import {
  isExecutionEnabled,
  isLanguageRolledOut,
  LANGUAGE_OPTIONS,
} from '../editor-languages'
import { DEFAULT_SNIPPETS } from '../editor-snippets'
import { useErrorMarkerOptional } from '../error-marker-context'
import type { ExecutionResult, RunExecutionResult } from '../execution-context'
import { useExecutionOptional } from '../execution-context'
import {
  type ErrorCode,
  formatToast,
  parseLegacyFormatError,
  type ServerErrorCode,
  shouldFallbackToMonaco,
} from '../format-toast'
import { useProblemOptional } from '../problem-context'
import { useSessionOptional } from '../session-context'
import { useTimerOptional } from '../timer/timer-context'
import {
  buildLineDecoration,
  buildMonacoMarkerData,
  ERROR_MARKER_OWNER,
} from './error-marker-monaco'

export type { RunClassTestCase, RunTestCase } from '@/lib/types/test-cases'
// Re-export for backwards compatibility (other files import from here)
export type { ExecutionResult } from '../execution-context'

// The formatter is a separately deployed, independently versioned microservice
// (services/formatter/). Validate its success body at the boundary rather than
// asserting the shape — if `formatted` is ever missing (JSON.stringify drops an
// `undefined` key, so a `{}` reaches us), a blind cast would report a silent
// `{ kind: 'ok', formatted: '' }` no-op instead of a visible error.
const FormatResponseSchema = z.object({ formatted: z.string() })

/**
 * Events yielded by the `execution.submitAsync` streaming mutation. Mirrors the
 * discriminated union the server yields (`api/_trpc/routers/execution.ts`) so
 * the client narrows on `type` from a checked shape instead of an `any` cast —
 * a field rename on either side then surfaces as a compile error here.
 */
type SubmitStreamEvent =
  | { type: 'status'; status: string; executionId: string }
  | ({ type: 'result' } & RunExecutionResult)
  | { type: 'error'; message: string }

// Language mapping for the formatter/execution API
const SUPPORTED_LANGUAGES: Record<LanguageValue, string> = {
  javascript: 'javascript',
  python: 'python',
  cpp: 'cpp',
  java: 'java',
  go: 'go',
}

type MonacoVimModule = {
  initVimMode: (
    editor: editor.IStandaloneCodeEditor,
    statusBar: HTMLElement,
  ) => { dispose: () => void }
}

const MonacoEditorLazy = lazy(
  () =>
    import('@monaco-editor/react') as Promise<{
      default: typeof import('@monaco-editor/react')['default']
    }>,
)

type MonacoEditorProps = ComponentProps<
  typeof import('@monaco-editor/react')['default']
>
type MonacoEditorRenderer = ComponentType<MonacoEditorProps>

/**
 * Monaco namespace type - provides access to KeyCode, KeyMod, and other Monaco constants
 * This is passed as the second argument to the onMount callback
 */
export type Monaco = Parameters<NonNullable<MonacoEditorProps['onMount']>>[1]

/**
 * Parameters for running test cases - supports both function and class-based tests
 */
export interface RunTestParams {
  /** Function-based test cases (mutually exclusive with classTestCases/rawTestScripts) */
  testCases?: RunTestCase[]
  /** Class-based test cases (mutually exclusive with testCases/rawTestScripts) */
  classTestCases?: RunClassTestCase[]
  /** Raw test scripts (mutually exclusive with testCases/classTestCases) */
  rawTestScripts?: string[]
}

export interface UseMonacoEditorResult {
  MonacoComponent: MonacoEditorRenderer
  isClient: boolean
  isDarkMode: boolean
  language: LanguageValue
  setLanguage: (value: LanguageValue) => void
  code: string
  setCode: (value: string) => void
  cursorPosition: { lineNumber: number; column: number } | null
  handleFormat: () => void
  handleReset: () => void
  /** Run code against custom test cases from the Test Cases pane */
  handleRun: (params: RunTestParams) => void
  /** Run code directly without test harness (interview mode) */
  handleRunRaw: () => Promise<void>
  /** Submit code for full evaluation against all DB test cases */
  handleSubmit: () => void
  isExecuting: boolean
  isSubmitting: boolean
  isFormatting: boolean
  executionResult: ExecutionResult | null
  executionError: Error | null
  executionMode: 'run' | 'submit' | 'raw' | null
  lastSubmitExecutionId: string | null
  editorRef: React.MutableRefObject<editor.IStandaloneCodeEditor | null>
  handleDidMount: (
    instance: editor.IStandaloneCodeEditor,
    monaco: Monaco,
  ) => void
  handleChange: (value: string | undefined) => void
  /**
   * True when the user's Vim keybinding preference could not be applied
   * (e.g. a post-deploy ChunkLoadError on the `monaco-vim` dynamic import).
   * Lets the UI tell the user their preference silently fell back to standard
   * keybindings instead of leaving them to discover it by typing.
   */
  vimInitFailed: boolean
}

export function useMonacoEditor(): UseMonacoEditorResult {
  const [isClient, setIsClient] = useState(false)
  const { settings, updateSettings } = useUserSettingsOptional()
  const [language, setLanguageState] = useState<LanguageValue>(
    // Ensure the stored language is one we support in the editor
    (['python', 'javascript', 'cpp', 'java', 'go'] as LanguageValue[]).includes(
      settings.editor.defaultLanguage as LanguageValue,
    )
      ? (settings.editor.defaultLanguage as LanguageValue)
      : 'python',
  )
  const [code, setCode] = useState<string>(DEFAULT_SNIPPETS.python)
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null)
  const cursorListenerRef = useRef<{ dispose: () => void } | null>(null)
  const [isEditorReady, setIsEditorReady] = useState(false)
  const [cursorPosition, setCursorPosition] = useState<{
    lineNumber: number
    column: number
  } | null>(null)
  const [isFormatting, setIsFormatting] = useState(false)
  const { resolvedTheme } = useTheme()
  const isDarkMode = resolvedTheme === 'dark'
  const { getToken, isSignedIn } = useAuth()
  const problem = useProblemOptional()
  const sessionContext = useSessionOptional()

  const timerCtx = useTimerOptional()

  // Use ExecutionContext for execution state instead of local state
  const executionContext = useExecutionOptional()
  // Keep stable refs to avoid cascading callback recreation when context state changes
  const executionContextRef = useRef(executionContext)
  executionContextRef.current = executionContext
  const sessionContextRef = useRef(sessionContext)
  sessionContextRef.current = sessionContext

  // Error marker published by the Test Results pane (FailurePanel). Kept in a
  // ref too so handleChange can clear it without re-memoizing.
  const errorMarkerContext = useErrorMarkerOptional()
  const errorMarkerRef = useRef(errorMarkerContext)
  errorMarkerRef.current = errorMarkerContext
  const monacoRef = useRef<Monaco | null>(null)
  const errorDecorationsRef =
    useRef<editor.IEditorDecorationsCollection | null>(null)

  // tRPC mutations for code execution (auth handled by TRPCProvider)
  const trpcUtils = trpc.useUtils()
  const runMutation = trpc.execution.run.useMutation()
  const submitMutation = trpc.execution.submitAsync.useMutation()
  const runRawMutation = trpc.execution.runRaw.useMutation()

  // Monotonic id minted at each execution start and threaded through its
  // settle/stream calls as the race-guard token. Unique per run (not per
  // problem) so even a repeated-slug A→B→A navigation can't let an old
  // request settle a newer run.
  const executionTokenRef = useRef(0)

  const vimModeRef = useRef<{ dispose: () => void } | null>(null)
  const vimStatusBarRef = useRef<HTMLDivElement | null>(null)
  const monacoVimModuleRef = useRef<MonacoVimModule | null>(null)
  const vimInitRequestId = useRef(0)
  const [vimInitFailed, setVimInitFailed] = useState(false)
  const keyBindingRef = useRef(settings.editor.keyBinding)
  const isApplyingStoredLanguageRef = useRef(false)
  const hasUserSelectedLanguageRef = useRef(false)

  // Track if we've completed initial code setup from session (either loaded draft or set default)
  const initializedFromSession = useRef(false)
  // Track if user has ever interacted with the editor (typing, deleting, etc.)
  // Used in the load-draft effect to decide whether to preserve user code during language switch.
  // More reliable than initializedFromSession because it's set synchronously in handleChange.
  const userHasInteracted = useRef(false)
  // Track whether the latest code change came from user interaction (typing, format, retrieve)
  // vs programmatic update (load draft, reset to default)
  const isUserEdit = useRef(false)
  // Track if initial setup is complete (session loaded and initial code determined)
  // This prevents saving boilerplate before we've had a chance to load saved code
  const initialSetupComplete = useRef(false)
  // Last restoreSeq value the load-draft effect has acted on. Used to detect
  // when an explicit Restore action (notifyRestoreCompleted) requires bypassing
  // the post-setup guard so the editor reloads from the freshly-saved draft.
  const lastAppliedRestoreSeqRef = useRef(0)
  // Track which language the current code state belongs to
  // This prevents saving code from one language to another language's draft when switching
  const codeLanguageRef = useRef<LanguageValue>('python')
  // Track whether we've already fired the page-mount auto-format for the current
  // (problem, language) combination. Stored as a ref so it resets on unmount
  // (refresh / navigate-away-and-back re-triggers the format) without causing renders.
  const autoFormattedKey = useRef<string | null>(null)

  // Derived state from ExecutionContext
  const isExecuting = executionContext?.isExecuting ?? false
  const isSubmitting =
    (executionContext?.isExecuting &&
      executionContext?.executionMode === 'submit') ??
    false
  const executionResult = executionContext?.result ?? null
  const executionError = executionContext?.error ?? null
  const executionMode = executionContext?.executionMode ?? null
  const lastSubmitExecutionId = executionContext?.lastSubmitExecutionId ?? null

  const clearExecutionResult = useCallback(() => {
    executionContextRef.current?.clear()
  }, [])

  // Keep language in sync with the stored preference and, once per-problem
  // availability loads (LG-247), with what the problem can actually
  // execute: a language without artifacts would strand the user on a blank
  // editor whose Run/Submit only error on click. ONE effect decides the
  // target — a separate preference-sync and availability-fallback would
  // ping-pong forever when the stored default isn't executable for this
  // problem. Skipped once the user has typed: their buffer is left alone
  // and the click-time gate explains.
  useEffect(() => {
    if (userHasInteracted.current) return
    const stored = (
      ['python', 'javascript', 'cpp', 'java', 'go'] as LanguageValue[]
    ).includes(settings.editor.defaultLanguage as LanguageValue)
      ? (settings.editor.defaultLanguage as LanguageValue)
      : 'python'
    // An explicit selection this session outranks the stored preference.
    const preferred = hasUserSelectedLanguageRef.current ? language : stored
    const available = problem?.availableLanguages ?? null
    const target =
      available !== null && !isExecutionEnabled(preferred, available)
        ? (LANGUAGE_OPTIONS.find((option) =>
            isExecutionEnabled(option.value, available),
          )?.value ?? preferred)
        : preferred
    if (language !== target) {
      // Preference sync / availability fallback, not a user choice: mark it
      // so downstream effects don't treat it as interaction, and don't
      // persist it.
      isApplyingStoredLanguageRef.current = true
      setLanguageState(target)
    }
  }, [language, settings.editor.defaultLanguage, problem?.availableLanguages])

  const setLanguage = useCallback((value: LanguageValue) => {
    // Explicit user-initiated language changes should persist.
    hasUserSelectedLanguageRef.current = true
    isApplyingStoredLanguageRef.current = false
    setLanguageState(value)
  }, [])

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    keyBindingRef.current = settings.editor.keyBinding
  }, [settings.editor.keyBinding])

  // Reset initialization flags when problem changes
  const problemSlug = problem?.problemSlug
  // biome-ignore lint/correctness/useExhaustiveDependencies: problemSlug triggers reset when problem changes
  useEffect(() => {
    initializedFromSession.current = false
    userHasInteracted.current = false
    initialSetupComplete.current = false
    codeLanguageRef.current = language
    autoFormattedKey.current = null
  }, [problemSlug])

  // Load draft from session when session loads or language changes
  const sessionLoading = sessionContext?.loading
  const sessionId = sessionContext?.session?.id
  const getDraft = sessionContext?.getDraft
  const cancelPendingSave = sessionContext?.cancelPendingSave
  const setRestoredStatus = sessionContext?.setRestoredStatus
  // Bumping this counter (via notifyRestoreCompleted) re-runs this effect so
  // an explicit Restore action picks up the freshly-saved draft code.
  const restoreSeq = sessionContext?.restoreSeq ?? 0
  // Problem-specific starter code (e.g., "def two_sum(nums):\n    pass\n")
  const problemStarterCode = problem?.data?.testConfig?.starterCode
  // biome-ignore lint/correctness/useExhaustiveDependencies: sessionId/restoreSeq triggers reload when session changes
  useEffect(() => {
    if (!getDraft || sessionLoading) {
      return
    }

    // Map Monaco language to session language key
    const sessionLanguage = language === 'javascript' ? 'javascript' : language

    // Check if we're switching to a different language than our current code
    const isLanguageSwitch = codeLanguageRef.current !== language

    // An incremented restoreSeq means the user explicitly invoked a code
    // replacement (Restore from a session row, Transfer code to editor, Reset
    // workspace, Restore last submission). Treat that as a force-reload signal
    // that bypasses the post-setup guard below.
    const isExplicitRestoreSignal =
      restoreSeq !== lastAppliedRestoreSeqRef.current

    // After initial setup, only re-run for explicit language switches OR an
    // explicit Restore signal. Without this guard, downstream setSession calls
    // (e.g. from saveDraft's createSession path) would re-fire this effect and
    // overwrite the editor's current state (formatter output, in-flight user
    // edits, etc.) with either the saved draft or the default snippet —
    // neither of which reflects what the user is currently seeing.
    if (
      initialSetupComplete.current &&
      !isLanguageSwitch &&
      !isExplicitRestoreSignal
    ) {
      return
    }

    // Consume the restore signal so a subsequent re-fire driven by session
    // churn (sessionId change, getDraft identity churn) doesn't re-process it.
    if (isExplicitRestoreSignal) {
      lastAppliedRestoreSeqRef.current = restoreSeq
    }

    // Try to get draft from current session
    const draft = getDraft(sessionLanguage)

    if (draft) {
      // Cancel any pending saves to prevent stale data from overwriting the loaded draft
      cancelPendingSave?.()

      // Only update if the draft differs from current code
      // This prevents cursor reset while user is typing
      const currentEditorValue = editorRef.current?.getValue()
      if (currentEditorValue !== draft) {
        setCode(draft)
        setRestoredStatus?.()
      } else {
        codeLanguageRef.current = language
      }
      initializedFromSession.current = true
      initialSetupComplete.current = true
    } else if (!userHasInteracted.current || isLanguageSwitch) {
      // Load default snippet when:
      // 1. User hasn't interacted yet (no user code to preserve)
      // 2. Switching languages and no draft exists for new language
      //
      // NOTE: We intentionally do NOT check `!initializedFromSession.current` here.
      // That flag is only set when a saved draft is loaded. On first-visit (no session),
      // it stays false forever. If we included it, the load-draft effect would overwrite
      // user edits with the default snippet whenever a new session is auto-created
      // (sessionId changes → effect re-fires → getDraft returns null during the brief
      // window before the fire-and-forget save completes → defaults loaded over user code).
      // See: https://github.com/Beyond-Cracking-the-Coding-Interview/beyondctci/issues/295

      // Defer when the test-config query hasn't delivered starter code yet.
      // DEFAULT_SNIPPETS entries are '', so without this guard we'd setCode('')
      // and flip initialSetupComplete=true — and the post-setup guard above
      // would then block the re-fire when starter code finally arrives.
      if (problemStarterCode === undefined) {
        return
      }

      const defaultValue =
        problemStarterCode[language] || DEFAULT_SNIPPETS[language]

      // Cancel any pending saves when switching languages to prevent saving old language's code
      if (isLanguageSwitch) {
        cancelPendingSave?.()
      }

      setCode(defaultValue)

      // Reset interaction tracking for new language
      if (isLanguageSwitch) {
        userHasInteracted.current = false
      }
      // Mark setup complete - we've determined there's no saved draft, so boilerplate is correct
      initialSetupComplete.current = true
    }
  }, [
    language,
    sessionLoading,
    sessionId,
    getDraft,
    cancelPendingSave,
    setRestoredStatus,
    problemStarterCode,
    restoreSeq,
  ])

  // Save draft when user edits code (debounced in SessionProvider)
  useEffect(() => {
    if (!sessionContext || code == null) {
      return
    }

    // Only save on user edits, not programmatic updates (load/format/reset/retrieve)
    if (!isUserEdit.current) {
      return
    }

    // Don't save until initial setup is complete
    if (!initialSetupComplete.current) {
      return
    }

    // Don't save while session is still loading
    if (sessionContext.loading) {
      return
    }

    // Clear flag after all guards pass — if a guard fails, the flag stays true
    // so the next effect run (e.g., when session finishes loading) will retry
    isUserEdit.current = false

    // Map Monaco language to session language key
    const sessionLanguage = language === 'javascript' ? 'javascript' : language

    // Notify timer of user edit (any edit counts — typing, deleting, pasting)
    timerCtx?.onEditorChange(1)

    // Mark as initialized once user types beyond boilerplate
    const boilerplate =
      problemStarterCode?.[language] || DEFAULT_SNIPPETS[language]
    if (code.length > boilerplate.length) {
      initializedFromSession.current = true
    }

    // saveDraft debounces and auto-creates a session if none exists
    sessionContext.saveDraft(sessionLanguage, code)
  }, [code, language, sessionContext, problemStarterCode, timerCtx])

  useEffect(() => {
    return () => {
      cursorListenerRef.current?.dispose()
    }
  }, [])

  const handleFormat = useCallback(async () => {
    if (!isSignedIn) {
      const msg = formatToast('AUTH_REQUIRED')
      if (msg) toast.error(msg)
      return
    }

    // Buffer's language doesn't match the selected language yet (mid switch);
    // sending this pair would format one language's code as another. Bail
    // silently — the auto-format effect re-runs once the buffer catches up. (LG-268)
    if (codeLanguageRef.current !== language) return

    const currentCode = editorRef.current?.getValue() ?? code
    if (!currentCode.trim()) {
      const msg = formatToast('NO_CODE')
      if (msg) toast.error(msg)
      return
    }

    const formatterUrl = import.meta.env.VITE_FORMATTER_URL
    const tabWidth = settings.editor.tabSize

    // Map Monaco language to session language key (same mapping used in load-draft / save effects)
    const sessionLanguage = language === 'javascript' ? 'javascript' : language

    // Helper: apply formatted code to the editor as a SYSTEM edit.
    // Writes the formatted code into the session draft cache BEFORE updating editor
    // state so no concurrent effect can observe stale data.
    const applyFormatted = (formatted: string) => {
      if (!editorRef.current) return
      if (!formatted) return
      if (formatted === currentCode) return

      if (editorRef.current.getValue() !== currentCode) {
        // User typed during the format request. Don't silently overwrite their
        // keystrokes; surface a toast so they understand why no format applied.
        toast(
          'Formatting skipped — code changed while formatting was in flight.',
        )
        return
      }

      // SYSTEM EDIT: update session cache FIRST, then editor state.
      // We deliberately do NOT set isUserEdit.current = true.
      void sessionContextRef.current?.applySystemEdit(
        sessionLanguage,
        formatted,
      )
      setCode(formatted)
    }

    // Helper: format JS in-browser using Prettier (lazy-loaded)
    const formatJsInBrowser = async (): Promise<string | null> => {
      try {
        const [prettier, babelPlugin, estreePlugin] = await Promise.all([
          import('prettier/standalone'),
          import('prettier/plugins/babel'),
          import('prettier/plugins/estree'),
        ])
        const result = await prettier.format(currentCode, {
          parser: 'babel',
          plugins: [babelPlugin.default, estreePlugin.default],
          semi: false,
          singleQuote: true,
          trailingComma: 'all' as const,
          tabWidth,
          printWidth: 80,
        })
        return result
      } catch (err) {
        console.warn('[format] Browser Prettier failed:', err)
        return null
      }
    }

    // Helper: format via the microservice. Returns a discriminated result so
    // the orchestrator can decide whether to fall back to Monaco. Toasts are
    // surfaced here, driven by the server-supplied error code.
    type ServiceResult =
      | { kind: 'ok'; formatted: string }
      | { kind: 'error'; code: ErrorCode }

    const formatViaService = async (): Promise<ServiceResult> => {
      if (!formatterUrl) return { kind: 'error', code: 'INTERNAL' }

      const token = await getToken()
      if (!token) {
        const msg = formatToast('AUTH_REQUIRED')
        if (msg) toast.error(msg)
        return { kind: 'error', code: 'AUTH_REQUIRED' }
      }

      try {
        // tabWidth is forwarded to each language formatter on the server.
        // Note: Go ignores it — gofmt is unconfigurable and always emits
        // tabs. See services/formatter/src/formatters/go.ts for details.
        const response = await fetch(`${formatterUrl}/format`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: currentCode,
            language: SUPPORTED_LANGUAGES[language],
            tabWidth,
          }),
        })

        if (!response.ok) {
          const data = (await response.json().catch(() => null)) as {
            code?: ServerErrorCode
            error?: string
            line?: number
            column?: number
            reason?: string
          } | null

          // Prefer the structured response. If the server didn't classify
          // (e.g. an older build that only returns `{ error: "..." }`),
          // try to extract line/col/reason from the raw error string so the
          // user still gets a useful syntax toast instead of "Formatter
          // unavailable.".
          let code: ErrorCode = data?.code ?? 'UNKNOWN'
          let line = data?.line
          let column = data?.column
          let reason = data?.reason
          if (!data?.code) {
            const parsed = parseLegacyFormatError(data?.error)
            if (parsed) {
              code = parsed.code
              line = parsed.line
              column = parsed.column
              reason = parsed.reason
            }
          }

          const message = formatToast(code, line, column, reason)
          if (message) toast.error(message)

          return { kind: 'error', code }
        }

        const parsed = FormatResponseSchema.safeParse(await response.json())
        if (!parsed.success) {
          console.error(
            '[format] Unexpected formatter response shape:',
            parsed.error,
          )
          const message = formatToast('UNKNOWN')
          if (message) toast.error(message)
          return { kind: 'error', code: 'UNKNOWN' }
        }
        return { kind: 'ok', formatted: parsed.data.formatted }
      } catch (err) {
        console.error('[format] Service unreachable:', err)
        const message = formatToast('NETWORK_UNREACHABLE')
        if (message) toast.error(message)
        return { kind: 'error', code: 'NETWORK_UNREACHABLE' }
      }
    }

    // Helper: fall back to Monaco's built-in formatter (tier 3)
    const formatViaMonaco = () => {
      console.warn('[format] Falling back to Monaco built-in formatter')
      const action = editorRef.current?.getAction(
        'editor.action.formatDocument',
      )
      if (action) action.run()
    }

    setIsFormatting(true)
    try {
      // Tier 1 (JS only): browser-side Prettier
      if (language === 'javascript') {
        const browserResult = await formatJsInBrowser()
        if (browserResult !== null) {
          applyFormatted(browserResult)
          return
        }
        console.warn(
          '[format] JS browser format failed, falling back to service',
        )
      }

      // Tier 2: microservice
      const serviceResult = await formatViaService()
      if (serviceResult.kind === 'ok') {
        if (serviceResult.formatted) applyFormatted(serviceResult.formatted)
        return
      }

      // Tier 3: Monaco built-in — only when policy allows. Monaco can't
      // format Python/C++/Go/Java; for JS we only fall back on infra failures.
      if (shouldFallbackToMonaco(language, serviceResult.code)) {
        toast('Formatter unavailable — used basic formatter.')
        formatViaMonaco()
      }
    } finally {
      setIsFormatting(false)
    }
  }, [code, language, getToken, isSignedIn, settings.editor.tabSize])

  // Keep codeLanguageRef in lockstep with the buffer, NOT with `language`.
  // On a language switch `language` flips one commit before `code` catches up
  // (load-draft's setCode lands next commit). Keying this effect on [code] alone
  // means the ref stays pointed at the OLD language during that in-between commit
  // and only advances once the buffer actually holds the new language's code —
  // giving the auto-format effect and handleFormat a trustworthy "buffer matches
  // language?" signal (see LG-268).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on [code] only; language must NOT advance the ref until the buffer catches up
  useEffect(() => {
    codeLanguageRef.current = language
  }, [code])

  // Auto-format on every page-mount: once the initial code (stub or draft) is
  // in place, fire one format call as a system edit. Reuses handleFormat so we
  // inherit spinner, error toasts (formatToast), and tier-fallback policy.
  useEffect(() => {
    if (!isSignedIn) return // logged-out users skip silently (issue #426)
    if (!initialSetupComplete.current) return
    if (!code || !code.trim()) return

    // The buffer hasn't caught up to `language` yet (mid language switch). Bail
    // WITHOUT consuming autoFormattedKey so this re-fires once `code` lands in the
    // next commit and the (code ↔ language) pair is consistent. (LG-268)
    if (codeLanguageRef.current !== language) return

    const key = `${problemSlug}:${language}`
    if (autoFormattedKey.current === key) return // already formatted this mount
    autoFormattedKey.current = key

    void handleFormat()
    // `code` is in deps because initialSetupComplete is a ref; we need
    // the load-draft effect's setCode(initial) to re-trigger this effect.
  }, [problemSlug, language, code, handleFormat, isSignedIn])

  const handleReset = useCallback(() => {
    const defaultValue =
      problemStarterCode?.[language] || DEFAULT_SNIPPETS[language]
    // Clear any stale user-edit flag so the save effect doesn't save boilerplate
    isUserEdit.current = false
    setCode(defaultValue)
    userHasInteracted.current = false
    initializedFromSession.current = false
    codeLanguageRef.current = language
  }, [language, problemStarterCode])

  // Persist language preference when user changes via toolbar
  useEffect(() => {
    if (isApplyingStoredLanguageRef.current) {
      isApplyingStoredLanguageRef.current = false
      return
    }

    if (language !== settings.editor.defaultLanguage) {
      updateSettings({ editor: { defaultLanguage: language } })
    }
  }, [language, settings.editor.defaultLanguage, updateSettings])

  const handleRun = useCallback(
    async (params: RunTestParams) => {
      const execCtx = executionContextRef.current
      if (!execCtx) return
      if (isExecuting || isSubmitting) return // Prevent double-clicks

      const { testCases, classTestCases, rawTestScripts } = params
      const isClassBased = classTestCases && classTestCases.length > 0
      const isRaw = rawTestScripts && rawTestScripts.length > 0

      const currentCode = editorRef.current?.getValue() ?? code
      const problemSlug = problem?.problemSlug ?? null

      // Check if we have a problem selected
      if (!problemSlug) {
        execCtx.failValidation(
          new Error(
            'No problem selected. Please select a problem to run tests.',
          ),
        )
        return
      }

      // Execution gate: global rollout + per-problem availability (LG-247)
      if (!isExecutionEnabled(language, problem?.availableLanguages ?? null)) {
        execCtx.failValidation(
          new Error('This language is not yet available for this problem.'),
        )
        return
      }

      // Require at least one test case (function, class, or raw)
      const hasTestCases = isRaw
        ? rawTestScripts.length > 0
        : isClassBased
          ? classTestCases.length > 0
          : testCases && testCases.length > 0

      if (!hasTestCases) {
        execCtx.failValidation(
          new Error('No test cases to run. Add at least one test case.'),
        )
        return
      }

      // For class tests, require at least one operation in each test case
      if (isClassBased) {
        const hasOperations = classTestCases.every(
          (tc) => tc.operations && tc.operations.length > 0,
        )
        if (!hasOperations) {
          execCtx.failValidation(
            new Error(
              'Each test case must have at least one operation. Add operations to your test cases.',
            ),
          )
          return
        }
      }

      // Mint a unique token for this run and thread it through every settle so
      // a stale response can't land on a newer execution (even same-problem).
      const execToken = String((executionTokenRef.current += 1))
      execCtx.startExecution('run', execToken)

      try {
        // Ensure session exists before executing (auto-creates if needed)
        const sessCtx = sessionContextRef.current
        let currentSessionId = sessCtx?.session?.id
        if (!currentSessionId && sessCtx?.createSession) {
          const newSession = await sessCtx.createSession()
          currentSessionId = newSession?.id
        }

        // Save draft immediately so code isn't lost if user navigates away.
        // Await to guarantee the draft reaches the server before execution
        // continues — prevents race where user navigates away before save completes.
        if (currentSessionId && sessCtx) {
          await sessCtx.saveDraftImmediate(language, currentCode)
        }

        // Resume clock if paused — active execution means active work
        timerCtx?.startStopwatch()
        // Checkpoint timer state to DB before execution
        timerCtx?.saveTimerStateToDb?.()

        // Build mutation input based on test type
        const mutationInput: Record<string, unknown> = {
          code: currentCode,
          problemId: problemSlug,
          // isExecutionEnabled above narrowed language to the executable set
          language,
          sessionId: currentSessionId, // Link execution to session
        }

        if (isRaw) {
          mutationInput.rawTestScripts = rawTestScripts
        } else if (isClassBased) {
          // Transform class test cases to the format expected by the API
          // Filter out comments - they're display-only annotations, not execution
          mutationInput.classTestCases = classTestCases.map((tc) => ({
            constructorArgs: tc.constructorArgs,
            operations: tc.operations
              .filter((op) => op.type !== 'comment')
              .map((op) => ({
                method: (op as { method: string }).method,
                args: (op as { args: string }).args,
                expected: (op as { expected: string }).expected,
                expectsError: (op as { expectsError?: boolean }).expectsError,
              })),
          }))
        } else {
          mutationInput.testCases = testCases
        }

        // Call the synchronous run endpoint via tRPC mutation
        const data = await runMutation.mutateAsync(
          mutationInput as Parameters<typeof runMutation.mutateAsync>[0],
        )

        // Set the result (synchronous - no polling needed for run mode)
        execCtx.succeed(data as RunExecutionResult, execToken)

        // Invalidate session history so Attempts tab has fresh data
        trpcUtils.sessions.getHistory.invalidate()
      } catch (err) {
        console.error('Failed to execute code:', err)
        execCtx.fail(
          err instanceof Error ? err : new Error('Failed to execute code'),
          execToken,
        )
      }
    },
    [
      code,
      language,
      problem?.problemSlug,
      problem?.availableLanguages,
      runMutation,
      isExecuting,
      isSubmitting,
      clearExecutionResult,
      trpcUtils,
      timerCtx,
    ],
  )

  const handleRunRaw = useCallback(async () => {
    const execCtx = executionContextRef.current
    if (!execCtx) return
    if (isExecuting || isSubmitting) return

    const currentCode = editorRef.current?.getValue() ?? code
    const problemSlug = problem?.problemSlug ?? null

    if (!problemSlug) {
      execCtx.failValidation(
        new Error('No problem selected. Please select a problem to run code.'),
      )
      return
    }

    // Raw execution uses no per-problem artifacts — global rollout gate only
    if (!isLanguageRolledOut(language)) {
      execCtx.failValidation(
        new Error('This language is not yet enabled for code execution.'),
      )
      return
    }

    // Mint a unique token for this run and thread it through every settle so
    // a stale response can't land on a newer execution (even same-problem).
    const execToken = String((executionTokenRef.current += 1))
    execCtx.startExecution('raw', execToken)

    try {
      const sessCtx = sessionContextRef.current
      let currentSessionId = sessCtx?.session?.id
      if (!currentSessionId && sessCtx?.createSession) {
        const newSession = await sessCtx.createSession()
        currentSessionId = newSession?.id
      }

      const data = await runRawMutation.mutateAsync({
        code: currentCode,
        problemId: problemSlug,
        // isLanguageRolledOut above narrowed language to the executable set
        language,
        sessionId: currentSessionId,
      })

      execCtx.succeed(data, execToken)
    } catch (err) {
      console.error('Failed to run code:', err)
      execCtx.fail(
        err instanceof Error ? err : new Error('Failed to run code'),
        execToken,
      )
    } finally {
      execCtx.finishExecution(execToken)
    }
  }, [
    code,
    language,
    problem?.problemSlug,
    runRawMutation,
    isExecuting,
    isSubmitting,
    clearExecutionResult,
  ])

  const handleSubmit = useCallback(async () => {
    const execCtx = executionContextRef.current
    if (!execCtx) return
    if (isExecuting || isSubmitting) return // Prevent double-clicks

    const currentCode = editorRef.current?.getValue() ?? code
    const problemSlug = problem?.problemSlug ?? null

    // Check if we have a problem selected
    if (!problemSlug) {
      execCtx.failValidation(
        new Error('No problem selected. Please select a problem to submit.'),
      )
      return
    }

    // Execution gate: global rollout + per-problem availability (LG-247)
    if (!isExecutionEnabled(language, problem?.availableLanguages ?? null)) {
      execCtx.failValidation(
        new Error('This language is not yet available for this problem.'),
      )
      return
    }

    // Mint a unique token for this submission and thread it through every
    // settle/stream so a stale response can't land on a newer execution.
    const execToken = String((executionTokenRef.current += 1))
    execCtx.startExecution('submit', execToken)

    try {
      // Ensure session exists before submitting (auto-creates if needed)
      const sessCtx = sessionContextRef.current
      let currentSessionId = sessCtx?.session?.id
      if (!currentSessionId && sessCtx?.createSession) {
        const newSession = await sessCtx.createSession()
        currentSessionId = newSession?.id
      }

      // Save draft immediately so code isn't lost if user navigates away.
      // Await to guarantee persistence before submission continues.
      if (currentSessionId && sessCtx) {
        await sessCtx.saveDraftImmediate(language, currentCode)
      }

      // Resume clock if paused — active execution means active work
      timerCtx?.startStopwatch()
      // Checkpoint timer state to DB before submission
      timerCtx?.saveTimerStateToDb?.()

      // Call the streaming submit endpoint via tRPC mutation
      const stream = await submitMutation.mutateAsync({
        code: currentCode,
        problemId: problemSlug,
        // isExecutionEnabled above narrowed language to the executable set
        language,
        sessionId: currentSessionId,
      })

      // Iterate streaming events from the async generator
      for await (const event of stream as AsyncIterable<SubmitStreamEvent>) {
        if (event.type === 'status') {
          execCtx.reportStreamStatus(event.executionId, execToken)
        }
        if (event.type === 'result') {
          execCtx.reportStreamResult(event, execToken)
          trpcUtils.sessions.getHistory.invalidate()

          // Check if all tests passed (successful submit)
          const result = event
          const allPassed =
            result.results &&
            result.results.length > 0 &&
            result.results.every((t: { passed: boolean }) => t.passed)

          if (allPassed) {
            // Close the session this submit ran against (`sessCtx`, captured
            // at submit start) — the live ref may already point at another
            // problem's session if the user navigated mid-stream.
            sessCtx?.closeSession()
            // Reset or pause timer/stopwatch based on autoReset setting,
            // then persist final timer state to DB
            timerCtx?.onSessionComplete()
          }
        }
        if (event.type === 'error') {
          execCtx.fail(new Error(event.message), execToken)
        }
      }
    } catch (err) {
      console.error('Failed to submit code:', err)
      execCtx.fail(
        err instanceof Error ? err : new Error('Failed to submit code'),
        execToken,
      )
    } finally {
      execCtx.finishExecution(execToken)
    }
  }, [
    code,
    language,
    problem?.problemSlug,
    problem?.availableLanguages,
    submitMutation,
    isExecuting,
    isSubmitting,
    clearExecutionResult,
    trpcUtils,
    timerCtx,
  ])

  const handleDidMount = useCallback(
    (editorInstance: editor.IStandaloneCodeEditor, monacoInstance: Monaco) => {
      editorRef.current = editorInstance
      monacoRef.current = monacoInstance
      setIsEditorReady(true)
      cursorListenerRef.current?.dispose()
      cursorListenerRef.current = editorInstance.onDidChangeCursorPosition(
        (event) => {
          setCursorPosition(event.position)
        },
      )
      const pos = editorInstance.getPosition()
      if (pos) {
        setCursorPosition(pos)
      }
    },
    [],
  )

  const handleChange = useCallback(
    (value: string | undefined) => {
      if (typeof value === 'string') {
        isUserEdit.current = true
        setCode(value)
        userHasInteracted.current = true
        codeLanguageRef.current = language
        // A runtime-error marker refers to the code as it was when it ran;
        // the first edit invalidates it (spec: cleared on the next edit).
        errorMarkerRef.current?.setMarker(null)
      }
    },
    [language],
  )

  // Render the published error marker as a red gutter strip + squiggle on the
  // innermost user frame's line. Cleared whenever the marker is set to null
  // (user edit, tab switch away from the failing case, or new execution).
  const activeErrorMarker = errorMarkerContext?.marker ?? null
  useEffect(() => {
    const editorInstance = editorRef.current
    const monaco = monacoRef.current
    if (!editorInstance || !monaco || !isEditorReady) return
    const model = editorInstance.getModel()
    if (!model) return

    if (!errorDecorationsRef.current) {
      errorDecorationsRef.current = editorInstance.createDecorationsCollection(
        [],
      )
    }

    if (
      activeErrorMarker &&
      activeErrorMarker.line >= 1 &&
      activeErrorMarker.line <= model.getLineCount()
    ) {
      monaco.editor.setModelMarkers(model, ERROR_MARKER_OWNER, [
        buildMonacoMarkerData(
          activeErrorMarker,
          model.getLineLength(activeErrorMarker.line),
        ),
      ])
      errorDecorationsRef.current.set([
        buildLineDecoration(activeErrorMarker.line),
      ])
    } else {
      monaco.editor.setModelMarkers(model, ERROR_MARKER_OWNER, [])
      errorDecorationsRef.current.clear()
    }

    // Teardown: the model can outlive this editor instance (mobile/desktop
    // swap remounts the editor), so never leave markers on it.
    return () => {
      if (!model.isDisposed()) {
        monaco.editor.setModelMarkers(model, ERROR_MARKER_OWNER, [])
      }
      errorDecorationsRef.current?.clear()
    }
  }, [activeErrorMarker, isEditorReady])

  // Apply/tear down keybinding mode whenever preference changes
  useEffect(() => {
    const applyKeyBinding = async () => {
      const editorInstance = editorRef.current
      if (!editorInstance || !isEditorReady) return

      // Standard mode: dispose any existing vim mode
      if (settings.editor.keyBinding === 'standard') {
        vimInitRequestId.current += 1
        vimModeRef.current?.dispose()
        vimModeRef.current = null
        setVimInitFailed(false)
        return
      }

      // Vim mode
      const requestId = vimInitRequestId.current + 1
      vimInitRequestId.current = requestId
      try {
        if (!monacoVimModuleRef.current) {
          monacoVimModuleRef.current = await import('monaco-vim')
        }
        if (vimInitRequestId.current !== requestId) return
        if (keyBindingRef.current !== 'vim') return
        if (editorRef.current !== editorInstance) return
        if (!vimStatusBarRef.current) {
          const statusEl = document.createElement('div')
          statusEl.style.position = 'absolute'
          statusEl.style.bottom = '4px'
          statusEl.style.left = '8px'
          statusEl.style.fontSize = '11px'
          statusEl.style.opacity = '0.7'
          statusEl.style.pointerEvents = 'none'
          editorInstance.getDomNode()?.appendChild(statusEl)
          vimStatusBarRef.current = statusEl
        }
        // If already active, dispose before re-init to avoid duplicate handlers
        vimModeRef.current?.dispose()
        vimModeRef.current = monacoVimModuleRef.current.initVimMode(
          editorInstance,
          vimStatusBarRef.current,
        )
        setVimInitFailed(false)
      } catch (err) {
        // A stale attempt is not a real failure — a newer effect run owns the
        // UI state, so don't flag it.
        if (vimInitRequestId.current !== requestId) return
        // The Vim binding never applied (most likely a post-deploy
        // ChunkLoadError on the dynamic import). Without surfacing it the
        // editor silently falls back to standard keybindings and the user's
        // typed Vim commands land as literal text. Report it and flag the UI.
        Sentry.captureException(err)
        console.error('Failed to enable Vim mode', err)
        setVimInitFailed(true)
      }
    }

    applyKeyBinding()

    return () => {
      // Cleanup only when unmounting or keybinding changes again
      // (effect re-runs so dispose happens above when switching to standard)
    }
  }, [isEditorReady, settings.editor.keyBinding])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      vimModeRef.current?.dispose()
      vimModeRef.current = null
      if (vimStatusBarRef.current?.parentNode) {
        vimStatusBarRef.current.parentNode.removeChild(vimStatusBarRef.current)
      }
      vimStatusBarRef.current = null
    }
  }, [])

  return useMemo(
    () => ({
      MonacoComponent: MonacoEditorLazy,
      isClient,
      isDarkMode,
      language,
      setLanguage,
      code,
      setCode,
      cursorPosition,
      handleFormat,
      handleReset,
      handleRun,
      handleRunRaw,
      handleSubmit,
      isExecuting,
      isSubmitting,
      isFormatting,
      executionResult,
      executionError,
      executionMode,
      lastSubmitExecutionId,
      editorRef,
      handleDidMount,
      handleChange,
      vimInitFailed,
    }),
    [
      isClient,
      isDarkMode,
      language,
      setLanguage,
      code,
      cursorPosition,
      handleFormat,
      handleReset,
      handleRun,
      handleRunRaw,
      handleSubmit,
      isExecuting,
      isSubmitting,
      isFormatting,
      executionResult,
      executionError,
      executionMode,
      lastSubmitExecutionId,
      handleDidMount,
      handleChange,
      vimInitFailed,
    ],
  )
}
